// Generated from scripts/countdown.ts. Rebuild with pnpm scripts:build.
/**
 * Countdown.
 *
 * Liquid renders the remaining time once, in the shop's time zone, and writes the end as a UTC epoch in
 * `data-end`; the browser never parses the merchant's date text. Shopify can serve that HTML from a cache, so
 * the rendered values may be minutes old: on connect this module recomputes every value from the epoch and
 * `Date.now()` before it starts ticking. It trusts the visitor's clock, which Liquid's render time cannot replace
 * on a cached page.
 *
 * One timeout per instance runs only while the instance intersects the viewport and the document is visible. It
 * lands just after the next value change (every second when seconds are shown, otherwise every minute), and each
 * tick recomputes the display from the clock and sets the next timeout from the clock too, so the display never
 * skips or doubles a value and a background tab or a throttled timer catches up without drift.
 *
 * The visual cells are hidden from assistive technology. The host becomes a `role="timer"` whose `aria-label`
 * states the remaining time in words at minute granularity, so a screen reader never hears the seconds. The words
 * come from `Intl` (unit names and plural rules of the page language), with the sentence from a translated
 * template in `data-remaining-label`.
 */
const UNITS = [
    { name: 'days', seconds: 86_400, intl: 'day' },
    { name: 'hours', seconds: 3_600, intl: 'hour' },
    { name: 'minutes', seconds: 60, intl: 'minute' },
    { name: 'seconds', seconds: 1, intl: 'second' },
];
/** Ticks land this long after a value changes, so a timer that fires a little early still shows the new value. */
const TICK_MARGIN_MS = 40;
const EPOCH_PATTERN = /^\d{9,11}$/;
/** Where the translated sentence in `data-remaining-label` takes the time in words; no braces, which end a Liquid tag. */
const TIME_TOKEN = '[time]';
/** The page language when the browser knows it; a malformed tag falls back to the browser's own language. */
function pageLanguage() {
    const language = document.documentElement.lang;
    try {
        return language ? Intl.getCanonicalLocales(language)[0] : undefined;
    }
    catch {
        return undefined;
    }
}
export class WontioCountdown extends HTMLElement {
    #lifetime;
    #observer;
    #timer;
    #endMs = 0;
    #cells = [];
    #labelUnits = [];
    #cellsHost;
    #message;
    #language;
    #numberFormats = new Map();
    #list;
    #labelMinute = -1;
    #inView = true;
    #ended = false;
    connectedCallback() {
        this.#teardown();
        const end = this.dataset.end ?? '';
        this.#cellsHost = this.querySelector('[data-countdown-cells]') ?? undefined;
        this.#message = this.querySelector('[data-countdown-ended]') ?? undefined;
        this.#cells = this.#readCells();
        if (!EPOCH_PATTERN.test(end) || !this.#cellsHost || !this.#cells.length)
            return;
        this.#endMs = Number(end) * 1000;
        this.#ended = false;
        // The label names the shown units down to the minute; with seconds alone it counts whole minutes.
        const shown = this.#cells.map(cell => cell.unit).filter(unit => unit.name !== 'seconds');
        this.#labelUnits = shown.length ? shown : UNITS.filter(unit => unit.name === 'minutes');
        this.#language = pageLanguage();
        this.#numberFormats.clear();
        this.#labelMinute = -1;
        this.#list =
            typeof Intl.ListFormat === 'function' ? new Intl.ListFormat(this.#language, { style: 'long', type: 'unit' }) : undefined;
        this.setAttribute('role', 'timer');
        this.#cellsHost.setAttribute('aria-hidden', 'true');
        this.#lifetime = new AbortController();
        const options = { signal: this.#lifetime.signal };
        document.addEventListener('visibilitychange', this.#onVisibility, options);
        // A page restored from the back-forward cache keeps its old numbers until this runs.
        window.addEventListener('pageshow', this.#onPageShow, options);
        if (typeof IntersectionObserver === 'function') {
            this.#observer = new IntersectionObserver(this.#onIntersect);
            this.#observer.observe(this);
        }
        this.#sync();
    }
    disconnectedCallback() {
        this.#teardown();
    }
    #teardown() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#observer?.disconnect();
        this.#observer = undefined;
        this.#clearTimer();
    }
    #readCells() {
        const cells = [];
        for (const element of this.querySelectorAll('[data-countdown-unit]')) {
            const unit = UNITS.find(candidate => candidate.name === element.dataset.countdownUnit);
            const value = element.querySelector('[data-countdown-value]');
            if (!unit || !value)
                continue;
            const text = value.firstChild instanceof Text ? value.firstChild : value.appendChild(document.createTextNode(''));
            cells.push({ unit, text, digits: Math.max(2, text.data.length) });
        }
        return cells.sort((a, b) => b.unit.seconds - a.unit.seconds);
    }
    /** The whole seconds left until the end, from the clock: never negative. */
    #remainingSeconds() {
        return Math.max(0, Math.floor((this.#endMs - Date.now()) / 1000));
    }
    /** Recomputes the display from the clock, then schedules the next tick if the instance is on screen. */
    #sync() {
        if (this.#ended)
            return;
        if (this.#endMs <= Date.now()) {
            this.#finish();
            return;
        }
        this.#render(this.#remainingSeconds());
        this.#schedule();
    }
    #tick = () => {
        this.#timer = undefined;
        this.#sync();
    };
    #render(remaining) {
        let rest = remaining;
        for (const cell of this.#cells) {
            const value = Math.floor(rest / cell.unit.seconds);
            rest -= value * cell.unit.seconds;
            const text = String(value).padStart(cell.digits, '0');
            if (cell.text.data !== text)
                cell.text.data = text;
        }
        // The words change at most once a minute, so the label is rebuilt and rewritten only then. The last minute
        // reads as one minute, never as zero.
        const minute = Math.max(1, Math.floor(remaining / 60));
        if (minute !== this.#labelMinute) {
            this.#labelMinute = minute;
            this.setAttribute('aria-label', this.#describe(minute * 60));
        }
    }
    /** The time in words, without the units that are zero, joined by the page language's list pattern. */
    #describe(seconds) {
        let rest = seconds;
        const parts = [];
        for (const unit of this.#labelUnits) {
            const value = Math.floor(rest / unit.seconds);
            rest -= value * unit.seconds;
            if (value > 0)
                parts.push(this.#format(unit, value));
        }
        const smallest = this.#labelUnits[this.#labelUnits.length - 1];
        if (!parts.length && smallest)
            parts.push(this.#format(smallest, 0));
        const time = this.#list ? this.#list.format(parts) : parts.join(', ');
        return (this.dataset.remainingLabel ?? TIME_TOKEN).replace(TIME_TOKEN, time);
    }
    /** Unit name and plural form for the page language, from the browser's locale data. */
    #format(unit, value) {
        let format = this.#numberFormats.get(unit.name);
        if (!format) {
            format = new Intl.NumberFormat(this.#language, { style: 'unit', unit: unit.intl, unitDisplay: 'long' });
            this.#numberFormats.set(unit.name, format);
        }
        return format.format(value);
    }
    /**
     * Sets the one timeout that ends just after the next value change. The period is a second while seconds are shown
     * and a minute otherwise; both measure from the end, which is where every unit boundary falls.
     */
    #schedule() {
        this.#clearTimer();
        if (this.#ended || !this.#inView || document.hidden || this.hidden)
            return;
        const period = this.#cells.some(cell => cell.unit.name === 'seconds') ? 1000 : 60_000;
        const untilChange = (this.#endMs - Date.now() + TICK_MARGIN_MS) % period || period;
        this.#timer = window.setTimeout(this.#tick, untilChange);
    }
    #clearTimer() {
        window.clearTimeout(this.#timer);
        this.#timer = undefined;
    }
    /** The end has passed: stop for good and show what the merchant chose. */
    #finish() {
        this.#ended = true;
        this.#teardown();
        if (this.dataset.endedAction === 'message' && this.#message && this.#cellsHost) {
            this.removeAttribute('role');
            this.removeAttribute('aria-label');
            this.#cellsHost.hidden = true;
            this.#message.hidden = false;
        }
        else {
            this.hidden = true;
        }
    }
    #onVisibility = () => {
        if (document.hidden)
            this.#clearTimer();
        else
            this.#sync();
    };
    #onPageShow = (event) => {
        if (event.persisted)
            this.#sync();
    };
    #onIntersect = (entries) => {
        const latest = entries[entries.length - 1];
        if (!latest)
            return;
        this.#inView = latest.isIntersecting;
        if (this.#inView)
            this.#sync();
        else
            this.#clearTimer();
    };
}
if (!customElements.get('wontio-countdown'))
    customElements.define('wontio-countdown', WontioCountdown);
